'use client'
import { useState } from 'react'
import Image from 'next/image'
import RemoveButton from '../remove-button'
import { CartContent } from '@/lib/api/shopware/cart/cart.types'
import Text from '../../text'
import ItemQuantity from '../item-quantity'
import Link from 'next/link'
import MonetaryValue from '../../monetary-value'
import { EligibilitySectionData } from '@/lib/api/strapi/eligibility-section/eligibility-section.types'
import { Dictionary } from '@/localization/dictionary'
import { generateSeoUrl } from '@/lib/utils/seo-url'
import { getOffersCustomFields } from '@/lib/api/shopware/products/cutom_fields.utils'
import { normalizeId } from '@/lib/utils/string'
import BundleContents from '@/components/composites/shopping-bag/cart-view/bundle-contents'

interface CartItemProps {
  data: CartContent['lineItems'][0]
  salesChannel: string,
  onClick?: () => void
  eligibilitySection?: EligibilitySectionData['attributes']
  showOfferPoints?: boolean,
  max?: number
  isQuantityLoading?: boolean
  onLoading?: (val: boolean) => void
  dict: Dictionary['shoppingBag']
}

const CartItem = ({
  data,
  salesChannel,
  onClick,
  eligibilitySection,
  showOfferPoints,
  max,
  isQuantityLoading,
  onLoading,
  dict,
}: CartItemProps) => {

  const slug = data.seoUrls?.[0]?.seoPathInfo || generateSeoUrl(data)
  const [loadingItems, setLoadingItems] = useState<string[]>([])

  const [color] = data.payload?.options?.map((option) => option.option) ?? []
  const { isCustomerOffer, isHostOffer, isFreeProduct, isKofferbon, customerOfferPoints } = getOffersCustomFields(data.payload, salesChannel)
  const showAlert = !(
    isCustomerOffer ||
    isHostOffer ||
    isFreeProduct
  )

  // Check if this is an event product by looking for the slot extension
  const isEvent = data.extensions && 'slot' in data.extensions

  // Create product URL with special handling for events
  const productUrl = isEvent
    ? `/event-details/${data.extensions?.event?.id}/${data.id}`
    : `/products/${slug}${color ? `?color=${color}` : ''}`

  const setItemLoadingState = (itemId: string, isLoading: boolean) => { 
    onLoading?.(isLoading)
    setLoadingItems((prev) => {
      if (isLoading) {
        return [...prev, itemId]
      } else {
        return prev.filter((id) => id !== itemId)
      }
    })
  }
  return (
    <div className={`border-b-[0.4px] border-secondary-200 bg-gray-100 px-2 py-4 ${loadingItems.includes(data.id) ? 'opacity-25' : ''}`}>
      <div className="flex items-center gap-2">
      <RemoveButton
        id={data.id}
        showAlert={showAlert}
        eligibilitySection={eligibilitySection}
      />
      <div className="aspect-[76/93] h-23 overflow-hidden">
        {data.cover && (
          <Image
            src={data.cover.url}
            width={76}
            height={93}
            alt={data.cover.alt ?? 'Product image'}
            className="h-full w-full object-cover"
          />
        )}
      </div>
      <div className="flex flex-1 flex-col gap-2">
        <div>
          <button onClick={() => onClick?.()}>
            <Link href={productUrl} className="hover:underline">
              <Text as="p" uppercase className="text-left leading-none">
                {data.label}
              </Text>
            </Link>
          </button>
          <div className="space-x-2 divide-x divide-secondary-100 leading-none">
            { data.payload?.customFields?.product_type && (
              <span className="text-2xs uppercase text-secondary-300">
              {data.payload?.customFields?.product_type}
            </span>
            )}
            <span className="px-2 text-2xs uppercase text-secondary-300">
              {`${data.price.referencePrice?.purchaseUnit ?? ''} ${data.price.referencePrice?.unitName ?? ''}`}
            </span>
          </div>
        </div>

        <div className="flex space-x-2 divide-x divide-secondary-100 gap-0.5">
          <p className="text-2xs text-secondary-300">
            {dict.tableView.item} {normalizeId(data.payload?.customFields?.sap_id) ?? '-'}
          </p>
          {showOfferPoints && !!customerOfferPoints && (
            <div className="flex items-center px-2">
              <Image
                priority
                src={'/icons/star-black.svg'}
                height={16}
                width={16}
                alt={'points'}
                className={`size-4 text-black`}
              />
              <Text className="text-2xs font-bold text-secondary-300">
                {customerOfferPoints}{' '}
                <span className="font-light">
                  {customerOfferPoints > 1
                    ? dict.customerOffer.pointPlural
                    : dict.customerOffer.point}
                </span>
              </Text>
            </div>
          )}
          {color && (
            <p className="text-2xs text-secondary-300">Colour {color}</p>
          )}
        </div>
        <div className="flex items-center justify-between gap-0.5">
          <Text as="p">
            <MonetaryValue value={data.price.totalPrice} />
          </Text>
          {isKofferbon
            ? <span className="text-sm font-semibold">1</span>
            : <ItemQuantity 
                  className="h-6 max-w-[calc(75/16*1rem)] shrink px-1 text-sm" iconSize={16} 
                  item={data} 
                  max={max} 
                  loading={isQuantityLoading} 
                  onLoading={(val) => setItemLoadingState(data.id, val)}  
              />
          }
        </div>
      </div>
      </div>

      {data.children?.length ? (
        <BundleContents
          items={data.children}
          dict={dict.tableView}
          className="mt-3"
        />
      ) : null}
    </div>
  )
}

export default CartItem
